import {createSlice, PayloadAction} from '@reduxjs/toolkit';

import {combineEpics} from 'redux-observable';
import {catchError, mergeMap, switchMap, map, filter} from 'rxjs/operators';
import {from, of} from 'rxjs';

import {isResponseError} from '~shared/utils';
import {MsDocAccessLevel} from '~shared/models/wiki';

import {batchActions} from '~ui/store/actions';

import {WikiEpic} from '../types';

enum EnsureAccessErrors {
    EnsureAccessUnsuccessful = 'ENSURE_ACCESS_UNSUCCESSFUL',
    General = 'GENERAL',
}

interface MsDocIFrameAccessState {
    [iframeSrc: string]: MsDocIFrameState;
}

interface MsDocIFrameState {
    count: number;
    level?: MsDocAccessLevel;
    error?: string;
    progress?: boolean;
}

interface MsDocIFrameSetPayload {
    id: number;
    iframeSrc: string;
}

interface MsDocAccessEnsurePayload {
    id: number;
    iframeSrc: string;
}

interface MsDocIFrameResPayload {
    iframeSrc: string;
    level: MsDocAccessLevel;
}

interface MsDocProgressSetPayload {
    iframeSrc: string;
    progress?: boolean;
}

interface MsDocIFrameDelPayload {
    iframeSrc: string;
}

interface MsDocEnsureAccessSuccessPayload {
    iframeSrc: string;
}

interface MsDocEnsureAccessErrorPayload {
    iframeSrc: string;
    error: string;
}

const initialState: MsDocIFrameAccessState = {};

const initialIframeState: MsDocIFrameState = {
    count: 1,
};

const {reducer, actions} = createSlice({
    name: 'ms-doc',
    initialState,
    reducers: {
        setMsDocIFrame(state, {payload: {iframeSrc}}: PayloadAction<MsDocIFrameSetPayload>) {
            const frame = state[iframeSrc];

            if (frame) {
                frame.count = frame.count + 1;

                return;
            }

            state[iframeSrc] = {
                ...initialIframeState,
            };
        },
        ensureMsDocAccess(state, {payload: {iframeSrc}}: PayloadAction<MsDocAccessEnsurePayload>) {
            const frame = state[iframeSrc];

            if (frame) {
                frame.count = frame.count + 1;
                frame.progress = true;

                return;
            }

            state[iframeSrc] = {
                ...initialIframeState,
                progress: true,
            };
        },
        resMsDocIFrame(state, {payload}: PayloadAction<MsDocIFrameResPayload>) {
            const {iframeSrc, level} = payload;
            const frame = state[iframeSrc];

            if (frame) {
                frame.level = level;
            }
        },
        delMsDocIFrame(state, {payload: {iframeSrc}}: PayloadAction<MsDocIFrameDelPayload>) {
            const frame = state[iframeSrc];

            if (!frame) {
                return;
            }

            frame.count = frame.count - 1;

            if (frame.count === 0) {
                delete state[iframeSrc];
            }
        },
        setMsDocProgress(
            state,
            {payload: {iframeSrc, progress}}: PayloadAction<MsDocProgressSetPayload>,
        ) {
            const frame = state[iframeSrc];

            if (frame) {
                frame.progress = progress;

                return;
            }

            state[iframeSrc] = {
                ...initialIframeState,
                progress,
            };
        },
        ensureMsDocAccessSuccess(
            state,
            {payload: {iframeSrc}}: PayloadAction<MsDocEnsureAccessSuccessPayload>,
        ) {
            const frame = state[iframeSrc];

            if (frame) {
                frame.error = undefined;
                frame.progress = false;

                return;
            }

            state[iframeSrc] = {
                ...initialIframeState,
                error: undefined,
                progress: false,
            };
        },
        ensureMsDocAccessError(
            state,
            {payload: {iframeSrc, error}}: PayloadAction<MsDocEnsureAccessErrorPayload>,
        ) {
            const frame = state[iframeSrc];

            if (frame) {
                frame.error = error;
                frame.progress = false;

                return;
            }

            state[iframeSrc] = {
                ...initialIframeState,
                error,
                progress: false,
            };
        },
    },
});

const setMsDocIFrameEpic: WikiEpic = (action$, _state$, {sdk}) =>
    action$.pipe(
        filter(actions.setMsDocIFrame.match),
        switchMap(({payload: {iframeSrc, id}}) => {
            return from(sdk.wiki.checkMsDocAccess({pageId: id})).pipe(
                map(({access_level: level}) => actions.resMsDocIFrame({iframeSrc, level})),
            );
        }),
        catchError((error, source) => {
            // Нам все-равно какая ошибка произошла, просто логируем
            console.error(error);

            return source;
        }),
    );

const ensureAccessEpic: WikiEpic = (action$, _state$, {sdk}) =>
    action$.pipe(
        filter(actions.ensureMsDocAccess.match),
        mergeMap((action) => {
            const {
                payload: {iframeSrc, id},
            } = action;

            return from(sdk.wiki.ensureMsDocAccess({pageId: id})).pipe(
                map(() => {
                    return batchActions([
                        actions.resMsDocIFrame({
                            iframeSrc,
                            level: 'read_write',
                        }),
                        actions.ensureMsDocAccessSuccess({iframeSrc}),
                    ]);
                }),
                catchError((error) => {
                    const code = isResponseError(error) ? error.code : EnsureAccessErrors.General;
                    const message =
                        code === EnsureAccessErrors.EnsureAccessUnsuccessful
                            ? 'ensure-access'
                            : 'default';

                    return of(actions.ensureMsDocAccessError({error: message, iframeSrc}));
                }),
            );
        }),
    );

const epic = combineEpics(setMsDocIFrameEpic, ensureAccessEpic);

export {reducer, epic, actions};
